import { PageHeading } from "@/components/ui/page";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Save } from "lucide-react";
import { usersAPI, rolesAPI, branchesAPI, getApiErrorPayload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { isValidSaudiMobile } from "@/lib/saudi-phone";
import { FieldError } from "@/components/ui/field-error";

interface Role {
	id: number;
	name: string;
	roleKey?: string;
	isDefault?: boolean;
}

interface Branch {
	id: number;
	name: string;
	isMain?: boolean;
}

/**
 * Simplified single-form employee creation: only full name + phone are
 * required; the server auto-generates credentials and resolves the default
 * role for everything left empty. Full editing stays in UsersDetailPage.
 */
const UserCreatePage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { toast } = useToast();
	const [saving, setSaving] = useState(false);
	const [roles, setRoles] = useState<Role[]>([]);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [form, setForm] = useState({
		fullName: "",
		phone: "",
		email: "",
		password: "",
		confirmPassword: "",
		roleId: "",
		branchId: "" as string,
		isActive: true,
	});
	const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});
	// Role and branch are preselected after load; only what the user types counts as a change.
	const { isDirty } = useIsDirty({
		fullName: form.fullName,
		phone: form.phone,
		email: form.email,
		password: form.password,
		confirmPassword: form.confirmPassword,
		isActive: form.isActive,
	});
	const { allowNavigation } = useUnsavedChangesGuard(isDirty && !saving);

	useEffect(() => {
		let cancelled = false;
		rolesAPI
			.getAll()
			.then((data: Role[]) => {
				if (cancelled || !Array.isArray(data)) return;
				setRoles(data);
				const preselected =
					data.find(role => role.roleKey === "cashier") ?? data.find(role => role.isDefault) ?? data[0];
				if (preselected) {
					setForm(prev => (prev.roleId ? prev : { ...prev, roleId: String(preselected.id) }));
				}
			})
			.catch(() => {
				/* The server still resolves a safe fallback role when the request omits roleId. */
			});
		branchesAPI
			.getAll()
			.then((data: Branch[]) => {
				if (cancelled || !Array.isArray(data) || data.length === 0) return;
				setBranches(data);
				const preselected = data.find(branch => branch.isMain) ?? data[0];
				setForm(prev => (prev.branchId ? prev : { ...prev, branchId: String(preselected.id) }));
			})
			.catch(() => {
				/* branch select stays empty */
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const handleChange = (field: keyof typeof form, value: string | boolean) => {
		setForm(prev => ({ ...prev, [field]: value }));
		setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
	};

	/** Field → i18n key of its first problem. */
	const validate = (): Partial<Record<keyof typeof form, string>> => {
		const next: Partial<Record<keyof typeof form, string>> = {};
		if (!form.fullName.trim()) next.fullName = "users.errors.fullNameRequired";
		if (!form.phone.trim()) next.phone = "users.errors.phoneRequired";
		else if (!isValidSaudiMobile(form.phone)) next.phone = "users.errors.phoneInvalid";
		if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
			next.email = "users.errors.invalidEmail";
		}
		if (form.password) {
			if (form.password.length < 6) next.password = "users.errors.passwordMinLength";
			else if (form.password !== form.confirmPassword) next.confirmPassword = "users.errors.passwordMismatch";
		}
		return next;
	};

	const handleSubmit = async () => {
		const validationErrors = validate();
		setErrors(validationErrors);
		if (Object.keys(validationErrors).length > 0) {
			toast({ title: t("common.error"), description: t("validation.checkFields"), variant: "destructive" });
			return;
		}

		// Only non-empty fields are sent; the server generates credentials and
		// resolves the default role for whatever is omitted.
		const payload: Record<string, unknown> = {
			fullName: form.fullName.trim(),
			phone: form.phone.trim(),
			status: form.isActive ? "active" : "inactive",
		};
		if (form.email.trim()) payload.email = form.email.trim();
		if (form.password) payload.password = form.password;
		if (form.roleId) payload.roleId = Number(form.roleId);
		if (form.branchId) payload.branchId = Number(form.branchId);

		try {
			setSaving(true);
			const created = await usersAPI.create(payload);
			toast({ title: t("common.success"), description: t("users.messages.created") });
			allowNavigation();
			// New employees need a PIN and a POS point before they can sign in at the cashier.
			navigate(created?.id ? `/users/${created.id}?setup=pin` : "/users");
		} catch (error) {
			const errorPayload = getApiErrorPayload(error);
			const errorMessage = t(errorPayload.key, (errorPayload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage === errorPayload.key ? t("users.errors.createFailed") : errorMessage,
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="container mx-auto py-6 space-y-6 max-w-full">
			<div className="flex items-center justify-between">
				<PageHeading className="">{t("users.create.title")}</PageHeading>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>{t("users.tabs.personalInfo")}</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<div className="space-y-2">
							<Label htmlFor="fullName">
								{t("users.form.fullName")} <span className="text-destructive">*</span>
							</Label>
							<Input
								id="fullName"
								value={form.fullName}
								onChange={e => handleChange("fullName", e.target.value)}
								placeholder={t("users.form.fullNamePlaceholder")}
								aria-invalid={!!errors.fullName}
							/>
							<FieldError message={errors.fullName} />
						</div>
						<div className="space-y-2">
							<Label htmlFor="phone">
								{t("users.form.phone")} <span className="text-destructive">*</span>
							</Label>
							<Input
								id="phone"
								value={form.phone}
								onChange={e => handleChange("phone", e.target.value)}
								placeholder={t("users.form.phonePlaceholder")}
								className="rtl:text-right"
								dir="ltr"
								aria-invalid={!!errors.phone}
							/>
							<FieldError message={errors.phone} />
						</div>
						<div className="space-y-2">
							<Label htmlFor="roleId">{t("users.form.role")}</Label>
							<Select value={form.roleId} onValueChange={value => handleChange("roleId", value)}>
								<SelectTrigger id="roleId">
									<SelectValue placeholder={t("users.form.selectRole")} />
								</SelectTrigger>
								<SelectContent>
									{roles.map(role => (
										<SelectItem key={role.id} value={String(role.id)}>
											{role.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="branchId">{t("users.form.branch")}</Label>
							<Select value={form.branchId} onValueChange={value => handleChange("branchId", value)}>
								<SelectTrigger id="branchId">
									<SelectValue placeholder={t("users.form.selectBranch")} />
								</SelectTrigger>
								<SelectContent>
									{branches.map(branch => (
										<SelectItem key={branch.id} value={String(branch.id)}>
											{branch.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label htmlFor="isActive">{t("users.create.status")}</Label>
							<div className="flex items-center gap-3 border border-border rounded-md p-3">
								<Switch
									id="isActive"
									checked={form.isActive}
									onCheckedChange={checked => handleChange("isActive", checked)}
								/>
								<Label htmlFor="isActive">
									{form.isActive ? t("users.form.active") : t("users.form.inactive")}
								</Label>
							</div>
						</div>
						<div className="space-y-2">
							<Label htmlFor="email">{t("users.form.email")}</Label>
							<Input
								id="email"
								type="email"
								value={form.email}
								onChange={e => handleChange("email", e.target.value)}
								placeholder={t("users.form.emailPlaceholder")}
								aria-invalid={!!errors.email}
							/>
							<FieldError message={errors.email} />
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">{t("users.form.password")}</Label>
							<Input
								id="password"
								type="password"
								value={form.password}
								onChange={e => handleChange("password", e.target.value)}
								placeholder={t("users.form.passwordPlaceholder")}
								aria-invalid={!!errors.password}
							/>
							<FieldError message={errors.password} />
							<p className="text-xs text-muted-foreground">{t("users.form.passwordOptionalHint")}</p>
						</div>
						<div className="space-y-2">
							<Label htmlFor="confirmPassword">{t("users.form.confirmPassword")}</Label>
							<Input
								id="confirmPassword"
								type="password"
								value={form.confirmPassword}
								onChange={e => handleChange("confirmPassword", e.target.value)}
								placeholder={t("users.form.confirmPasswordPlaceholder")}
								aria-invalid={!!errors.confirmPassword}
							/>
							<FieldError message={errors.confirmPassword} />
						</div>
					</div>

					<div className="flex pt-4">
						<Button className="px-12" onClick={handleSubmit} disabled={saving}>
							{saving ? (
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
							) : (
								<Save className="h-4 w-4 mx-2" />
							)}
							{t("common.save")}
						</Button>
					</div>
				</CardContent>
			</Card>
		</div>
	);
};

export default UserCreatePage;
